/**
 * The saved versions of a design, as a list somebody can read.
 *
 * The server has stored these since cloud save landed — `listVersions` returns
 * them, `pruneVersions` enforces the per-tier count, and `X-Device` is recorded
 * on every upload — and no screen has ever shown one. `upgrade-panel.ts` has
 * meanwhile been selling Creator as *"500 designs in the cloud, 25 versions of
 * each"*, which makes this the only paid feature with no interface at all.
 *
 * Pure: the formatting and the ordering are the parts that can be wrong in a
 * way nobody notices, so they live here and are tested without a browser.
 */

import { formatBytes, TIER_LIMITS, type Tier } from "../../shared/tiers";
import type { VersionSummary } from "../api/projects";

export interface VersionRow {
  id: string;
  revision: number;
  /** "Just now", "2 hours ago" — the relative form the rest of the HUD uses. */
  when: number;
  /** The device that saved it, or null when the server recorded none. */
  device: string | null;
  size: string;
  layerCount: number;
  /** The one the project currently points at. Cannot be restored onto itself. */
  current: boolean;
}

/**
 * Newest first, with the live one marked.
 *
 * Sorted by revision rather than by `created_at`: the timestamp is the server's
 * clock at the moment of the write, and two saves inside the same millisecond
 * would tie. Revision is the thing the server actually orders by and the thing
 * the conflict check compares, so it is the honest key.
 */
export function versionRows(
  versions: readonly VersionSummary[],
  currentRevision: number,
): VersionRow[] {
  return [...versions]
    .sort((a, b) => b.revision - a.revision)
    .map((v) => ({
      id: v.id,
      revision: v.revision,
      when: v.created_at,
      // "unknown" is what the server stores when no `X-Device` arrived. Showing
      // it verbatim tells the user something about our plumbing rather than
      // about their design.
      device: v.created_by && v.created_by !== "unknown" ? v.created_by : null,
      size: formatBytes(v.size_bytes),
      layerCount: v.layer_count,
      current: v.revision === currentRevision,
    }));
}

/**
 * The one line under a version's heading.
 *
 * Device first when there is one, because with several machines that is the
 * fact that distinguishes two versions saved minutes apart.
 */
export function describeVersion(row: VersionRow): string {
  const layers = `${row.layerCount} ${row.layerCount === 1 ? "layer" : "layers"}`;
  return [row.device, layers, row.size].filter(Boolean).join(" · ");
}

/**
 * What restoring does, said before it happens.
 *
 * Restoring is not a rewind — it saves the old contents as a *new* version, so
 * nothing is lost and the revision only ever goes up. Saying so is what stops
 * this reading as the destructive operation it looks like.
 */
export const RESTORE_NOTE =
  "Restoring saves this version as a new one, so nothing is lost and you can undo it.";

/**
 * Why this design has no version list, in the user's terms.
 *
 * Five reasons produce an empty panel and only one of them is "you are signed
 * out". Collapsing them into a single sentence — which is what the scaffold
 * did — tells someone whose plan is full that their work is on this device
 * only, which is both wrong and unactionable.
 *
 * Returns null when there is nothing to explain, i.e. when a list should show
 * instead. Callers treat null as "render the rows".
 */
export function explainStatus(status: CloudStatus, signedIn: boolean): string | null {
  switch (status.kind) {
    case "linked":
      return null;
    case "pending":
      return "Saving this design to your account…";
    case "plan-full":
      // The number comes from the tier, not from this sentence, so a plan
      // change cannot leave a stale figure behind in the copy.
      return "Your plan's designs are all in use, so this one is on this device only. Free up a slot or upgrade to keep versions of it.";
    case "denied":
      return "This design could not be saved to your account, so it is on this device only.";
    case "local":
      return signedIn
        ? "Versions start once this design saves to your account."
        : "Sign in to keep versions of this design. Right now it is on this device only.";
  }
}

/**
 * Where the list stops.
 *
 * Worth saying before somebody discovers it: the server prunes to the tier's
 * retention on every upload, so on free the fourth save silently removes the
 * first. A list that just gets shorter with no explanation looks like data
 * loss, because it is — it is simply *intended* data loss.
 */
export function retentionNote(tier: Tier): string {
  const keep = TIER_LIMITS[tier].versionRetention;
  return `Your plan keeps the last ${keep} versions of a design. Older ones are removed as you save.`;
}

/**
 * The accessible name for a row's Restore button.
 *
 * Every one of them reads "Restore", so a screen reader's button list is a
 * column of identical entries with no way to tell which is which — the exact
 * information the sighted layout carries in the row beside it.
 */
export function restoreLabel(row: VersionRow, when: string): string {
  return row.device
    ? `Restore the version saved ${when} on ${row.device}`
    : `Restore the version saved ${when}`;
}

/**
 * What the editor's cloud copy is doing, as the versions panel needs it.
 *
 * A refusal is cloud state too, and it exists precisely when there is no
 * project id — which is why this is a union rather than `string | null`.
 */
export type CloudStatus =
  | { kind: "local" }
  | { kind: "pending" }
  | { kind: "linked"; projectId: string }
  | { kind: "plan-full" }
  | { kind: "denied" };

/**
 * The exact moment, for the tooltip behind a relative time.
 *
 * Date *and* time. `formatDate` is date-only, which on this list is nearly
 * useless — the rows people need to tell apart are the ones saved minutes
 * apart on the same afternoon, and "9 August 2026" is the same string for all
 * of them. Not put in `dom.ts` beside `formatDate` because that one is the
 * fallback inside `formatRelative`, where a time would be noise.
 */
export function exactTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
